import React, { useEffect, useMemo, useState } from 'react';
import Page from 'components/Page';
import { Form, Input, Select, Radio, Button, Row, Col, Alert, Checkbox, Divider, InputNumber, Space } from 'antd';
import FormDependency from 'components/FormDependency';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Modal from 'components/Modal';
import FunnelStepTable from './FunnelStepTable';

const extraFields = `
  website {
    id
    matomoId
    matomoGoals {
      id
      matomoGoalId
      matomoSiteId
      name
      description
      matchAttribute
      pattern
      patternType
      caseSensitive
      allowMultipleConversionsPerVisit
      revenue
      useEventValueAsRevenue
      multiAttributionEnabled
      funnelActivated
      funnelSteps {
        position
        name
        pattern
        patternType
        required
      }
    }
  }
`;
const patternTypes = [
  {
    value: 'contains',
    label: 'that contains',
  },
  {
    value: 'exact',
    label: 'that is exactly',
  },
  {
    value: 'regex',
    label: 'that matches the expression',
  },
];

const triggerValues = [

  {
    value: 'whenVisitors',
    label: 'When visitor',
  },
  {
    value: 'manually',
    label: 'Manually',
  },

];

export const matchAttributes = [
  {
    value: 'url',
    label: 'visits a URL',
  },
  {
    value: 'visitPageTitle',
    label: 'visit a given page title',
  },
  {
    value: 'sendEvent',
    label: 'send an event',

  },
  {
    value: 'file',
    label: 'download a file',
  },
  {
    value: 'clickLink',
    label: 'click on link to an external website',
  },
  {
    value: 'stayForTime',
    label: 'stay for more than',
  }];

export const eventTypes = [
  {
    value: 'event_category',
    label: 'with an event category',
  },
  {
    value: 'event_action',
    label: 'with an event action',
  },
  {
    value: 'event_name',
    label: 'with an event name',
  },
];

const CREATE_GOAL = gql`
  mutation($websiteId: ID!, $input: MatomoGoalInput!) {
    createMatomoGoal(websiteId: $websiteId, input: $input)
  }
`;

const UPDATE_GOAL = gql`
  mutation($websiteId: ID!, $matomoGoalId: Int!, $input: MatomoGoalInput!) {
    updateMatomoGoal(websiteId: $websiteId, matomoGoalId: $matomoGoalId, input: $input)
  }
`;

const defaultGoalValues = {
  patternType: patternTypes[0].value,
  caseSensitive: false,
  allowMultipleConversionsPerVisit: false,
  useEventValueAsRevenue: false,
  funnelActivated: false,
  multiChannelAttribution: false,
  required: false,
  funnelSteps: [],
};

function getInitialFormValues(goal) {
  let matchAttribute = goal?.matchAttribute || matchAttributes[0].value;
  let triggerValue = triggerValues[0].value;
  let eventType = eventTypes[0].value;

  if (matchAttribute === 'manually') {
    triggerValue = 'manually';
  }

  if (eventTypes.find(t => t.value === matchAttribute)) {
    eventType = matchAttribute;
    matchAttribute = 'sendEvent';
  }

  return {
    ...defaultGoalValues,
    ...goal,
    triggerValue,
    eventType,
    matchAttribute,
  };
}

function getGoalInputValues({ triggerValue, eventType, pattern, patternType, ...values }) {
  let { matchAttribute } = values;
  if (triggerValue === 'manually') {
    matchAttribute = 'manually';
  } else if (matchAttribute === 'sendEvent') {
    matchAttribute = eventType;
  }
  return {
    ...values,
    matchAttribute,
    pattern: pattern?.toString() || '.*',
    patternType: patternType || 'regex',
  };
}

const getMatchingFunnelSteps = (funnelSteps, validationValue) => funnelSteps.map(r => {
  const { pattern } = r;

  let res;

  switch (r.patternType) {
    case 'url_equals':
    case 'path_equals':
    case 'eventname_equals':
    case 'eventcategory_equals':
    case 'eventaction_equals':
      res = validationValue === pattern;
      break;
    case 'url_contains':
    case 'path_contains':
    case 'eventcategory_contains':
    case 'eventname_contains':
    case 'eventaction_contains':
      res = validationValue?.includes(pattern);
      break;
    case 'url_startswith':
    case 'path_startswith':
    case 'eventcategory_startswith':
    case 'eventname_startswith':
    case 'eventaction_startswith':
      res = validationValue?.startsWith(pattern);
      break;
    case 'url_endswith':
    case 'path_endswith':
    case 'eventname_endswith':
    case 'eventaction_endswith':
    case 'eventcategory_endswith':
      res = validationValue?.endsWith(pattern);
      break;
    case 'url_regexp':
      res = new RegExp(pattern).test(validationValue);
      break;
    default:
      res = false;
  }
  return {
    ...r,
    isMatching: res,
  };
});

const EditGoal = () => {
  const { params } = useRouteMatch('/data-sources/:dataSourceId/analytics/settings/goals/:goalId');
  const { dataSource, refetch, loading } = useDataSource({ extraFields });
  const [form] = Form.useForm();
  const [createGoal, { loading: creating }] = useMutation(CREATE_GOAL);
  const [updateGoal, { loading: updating }] = useMutation(UPDATE_GOAL);
  const history = useHistory();

  const websiteId = dataSource?.website.id;
  const goal = dataSource?.website.matomoGoals.find(g => g.matomoGoalId.toString() === params.goalId);
  const funnelSteps = Form.useWatch('funnelSteps', form);
  const [stepValidationValue, setStepValidationValue] = useState('');
  const [editable, setEditable] = useState(!goal?.funnelActivated);
  const [validationResults, setValidationResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const onFinish = async (values) => {
    const input = getGoalInputValues(values);

    if (goal?.id) {
      await updateGoal({ variables: { websiteId, matomoGoalId: goal.matomoGoalId, input } });
    } else {
      await createGoal({ variables: { websiteId, input } });
    }
    refetch();
    history.push(`/data-sources/${dataSource.id}/analytics/settings/goals`);
  };

  const initialValues = useMemo(() => getInitialFormValues(goal));

  const handleCancel = () => {
    history.push(`/data-sources/${dataSource.id}/analytics/settings/goals`);
  };

  const toggleEditable = () => {
    setEditable(!editable);
  };

  useEffect(() => {
    if (funnelSteps?.length && stepValidationValue?.length) {
      setValidationResults(getMatchingFunnelSteps(funnelSteps, stepValidationValue));
    }
  }, [stepValidationValue, funnelSteps]);

  const handleUnlockFunnel = () => {
    setModalOpen(true);
  };

  return (
    <Page loading={loading}>
      <Page.Content title="Add a new goal">
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          initialValues={initialValues}
        >
          <Form.Item
            name="name"
            label="Goal Name"
            rules={[{ required: true, message: 'Please enter goal name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input />
          </Form.Item>
          <Row gutter={[16, 16]}>
            <Col span={4}>
              <Form.Item name="triggerValue" label="Goal is triggered">
                <Select options={triggerValues} />
              </Form.Item>
            </Col>

            <FormDependency name="triggerValue" show={value => value === 'manually'}>
              <Col span={20}>
                <Alert
                  description={(
                    <>
                      The visited page needs to contain a call to the JavaScript &apos;trackGoal&apos; method
                      <a href="https://developer.matomo.org/guides/tracking-javascript-guide?mtm_campaign=Matomo_App&mtm_source=Matomo_App_OnPremise&mtm_medium=App.CoreHome.index#manually-trigger-goal-conversions" target="_blank" rel="noopener noreferrer">
                        (learn more)
                      </a>
                    </>
                  )}
                  type="info"
                  showIcon
                />
              </Col>
            </FormDependency>
            <FormDependency name="triggerValue" show={value => value === 'whenVisitors'}>
              <Col span={6}>
                <Form.Item name="matchAttribute" label=" ">
                  <Select options={matchAttributes} />
                </Form.Item>
              </Col>

              <FormDependency name="matchAttribute" show={value => value !== 'sendEvent' && value !== 'stayForTime'}>
                <Col span={4}>
                  <Form.Item name="patternType" label=" ">
                    <Select options={patternTypes} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="pattern" label=" " rules={[{ required: true, message: 'Pattern is required' }]}>
                    <Input placeholder="pattern" rules={[{ required: true, message: 'Pattern is required' }]} />
                  </Form.Item>
                </Col>
              </FormDependency>

              <FormDependency name="matchAttribute" show={value => value === 'sendEvent'}>
                <Col span={4}>
                  <Form.Item name="eventType" label=" ">
                    <Select options={eventTypes} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="patternType" label=" ">
                    <Select options={patternTypes} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="pattern" label=" ">
                    <Input placeholder="pattern" />
                  </Form.Item>
                </Col>
              </FormDependency>
              <FormDependency name="matchAttribute" show={value => value === 'stayForTime'}>
                <Col span={4}>
                  <Form.Item
                    name="pattern"
                    label=" "
                  >
                    <InputNumber min={0} addonAfter="minutes" />
                  </Form.Item>
                </Col>
              </FormDependency>

              <FormDependency name="matchAttribute" show={value => value === 'url'}>
                <Col span={24}>
                  <Alert
                    description={(
                      <>
                        eg. contains &apos;checkout/confirmation&apos;
                        <br />
                        eg. is exactly &apos;http://example.com/thank-you.html&apos;
                        <br />
                        eg. matches the expression eg. contains&apos;Order confirmation&apos;(.*)\/demo\/(.*)&apos;
                      </>
                    )}
                    type="info"
                    showIcon
                  />
                </Col>
              </FormDependency>
              <FormDependency name="matchAttribute" show={value => value === 'visitPageTitle'}>
                <Col span={24}>
                  <Alert
                    description=" eg. contains &apos; Order confirmation &apos;"
                    type="info"
                    showIcon
                  />
                </Col>
              </FormDependency>
              <FormDependency name="matchAttribute" show={value => value === 'sendEvent'}>
                <Col span={24}>
                  <Alert
                    description={(
                      <>
                        eg. contains &apos;video&apos;
                        <br />
                        eg. is exactly &apos;click&apos;
                        <br />
                        eg. matches the expression &apos;(.*)_banner&apos;
                      </>
                    )}
                    type="info"
                    showIcon
                  />
                </Col>
              </FormDependency>
              <FormDependency name="matchAttribute" show={value => value === 'file'}>
                <Col span={24}>
                  <Alert
                    description={(
                      <>
                        eg. contains &apos;files/brochure.pdf&apos;
                        <br />
                        eg. is exactly &apos;http://example.com/files/brochure.pdf&apos;
                        <br />
                        eg. matches the expression &apos;(.*)\.zip&apos;
                      </>
                    )}
                    type="info"
                    showIcon
                  />
                </Col>
              </FormDependency>
              <FormDependency name="matchAttribute" show={value => value === 'clickLink'}>
                <Col span={24}>
                  <Alert
                    description={(
                      <>
                        eg. contains &apos;amazon.com&apos;
                        <br />
                        eg. is exactly &apos;http://mypartner.com/landing.html&apos;
                        <br />
                        eg. matches the expression &apos;http://www.amazon.com\/(.*)\/yourAffiliateId&apos;
                      </>
                    )}
                    type="info"
                    showIcon
                  />
                </Col>
              </FormDependency>
              <FormDependency name="matchAttribute" show={value => value === 'stayForTime'}>
                <Col span={24}>
                  <Alert
                    description="eg. at least 5 minutes. Use decimals to match seconds, for example, 0.5 to match 30 seconds."
                    type="info"
                    showIcon
                  />
                </Col>
              </FormDependency>
              <FormDependency name="matchAttributes" show={value => value !== 'stayForTime'}>
                <Col span={24}>
                  <Form.Item name="caseSensitive" valuePropName="checked">
                    <Checkbox>
                      Case sensitive match (optional)
                    </Checkbox>
                  </Form.Item>
                </Col>
              </FormDependency>
            </FormDependency>
            <FormDependency name="matchAttributes" show={value => value !== 'stayForTime'}>
              <Col span={8}>
                <Form.Item name="allowMultipleConversionsPerVisit" label="Allow multiple conversions per visit">
                  <Radio.Group>
                    <Radio value={false}>(default) Goal can only be converted once per visit</Radio>
                    <Radio value>Allow Goal to be coverted more than per visit </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={16} style={{ display: 'flex', alignItems: 'center' }}>
                <Alert style={{ flexGrow: '1' }} message="If a Page matching this Goal is refreshed or viewed more than once in a Visit, the Goal will only be tracked the first time the page was loaded during this visit." />
              </Col>
            </FormDependency>

            <Col span={8}>
              <Form.Item label="Goal Revenue (optional)" name="revenue">
                <InputNumber />
              </Form.Item>
            </Col>

            <Col span={16} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="For example, a Contact Form submitted by a visitor may be worth $10 on average. Matomo will help you understand how well your visitors segments are performing." />
            </Col>
            <FormDependency name="matchAttribute" show={value => value === 'sendEvent' && value !== 'manually'}>
              <Col span={8}>
                <Form.Item name="useEventValueAsRevenue" valuePropName="checked">
                  <Checkbox>Use the event value(if it exists) as the goal conversion revenue</Checkbox>
                </Form.Item>
              </Col>
              <Col span={16} style={{ display: 'flex', alignItems: 'center' }}>
                <Alert
                  style={{ flexGrow: '1' }}
                  message={(
                    <>
                      <p>
                        If the event you are matching has a revenue, and that revenue is tracked as the event value, you can enable this option to record the event value as the goal conversion&apos;s revenue. If your goal revenue will not vary per conversion, you can ignore this option and just set a default revenue above.
                      </p>
                      <p>
                        Note: If both a default goal revenue and event value are defined, the event value will be used. If this option is enabled and no event value is sent in a request, the default revenue will be used (if defined).
                      </p>
                    </>
                  )}
                />
              </Col>
            </FormDependency>
            <Divider style={{ margin: 10 }} />
          </Row>

          <h2>Funnel</h2>

          <Row gutter={[16, 16]}>

            <Col span={24}>
              <p>
                A Funnel defines a series of actions that you expect your visitors to take on their way to converting a goal. By defining a funnel you can improve your conversion rates and revenue as you can determine where you lose your visitors in converting your goal.
              </p>
              <ul>
                <li>Find out where your visitors have problems</li>
                <li>Find out where they don&apos;t understand the flow of your website</li>
                <li>Find out where a bug on your website or app occurs</li>
                <li>Find other problems that get in the way of converting your goals</li>
              </ul>
              <p>
                To setup a funnel, follow the steps below:
              </p>
            </Col>

            <Col span={24}>

              <h3>1. Configure funnel steps</h3>
              <p>
                To make up the steps for your funnel we recommend to make notes of the URLs for each step while you click through your website. The final step of the funnel is your goal being configured here.
              </p>
              <p>
                If you mark a step as &apos;required&apos; then the funnel report will only include visits who converted the goal via that required step.
              </p>
              <p>
                The URL validator below tells you which steps of your funnel will be matched for a given page URL. It will show a list of URLs that match the configuration and had been viewed by your visitors in the last few days.
              </p>
              <Form.List name="funnelSteps">
                {(fields, { add, remove }) => (
                  <Form.Item>

                    {!editable ? (
                      <>
                        <Form.Item>
                          <FunnelStepTable fields={fields} remove={remove} disabled={!editable} validationResults={validationResults} />
                        </Form.Item>
                        <Alert
                          description={(
                            <p>
                              This funnel is activated. In order to make changes to this funnel you need to &quot;unlock&quot; the funnel. When you make changes to an activated funnel, new reporting data needs to be generated and reports for this funnel in the past might no longer be available.
                            </p>
                          )}
                          type="info"
                          showIcon
                          action={(
                            <Space direction="vertical">
                              <Button type="primary" onClick={handleUnlockFunnel}>Unlock Funnel</Button>
                            </Space>
                          )}
                        />
                        <Modal
                          open={modalOpen}
                          closable={false}
                          onCancel={() => setModalOpen(false)}
                          footer={(
                            <Space>
                              <Button onClick={() => setModalOpen(false)}>No</Button>
                              <Button type="primary" onClick={toggleEditable}>Yes</Button>

                            </Space>
                          )}
                        >
                          Are you sure you want to unlock this funnel? When you update the funnel,
                          <br />
                          reports in the past for this funnel become unavailable.
                        </Modal>
                      </>
                    ) : (
                      <>
                        <Form.Item>
                          <FunnelStepTable fields={fields} remove={remove} validationResults={validationResults} />
                        </Form.Item>
                        <Form.Item>
                          <Button type="primary" onClick={() => add({ patternType: 'url_equals', required: false })}>
                            Add step
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.Item>
                )}
              </Form.List>
            </Col>
            <Col span={24}>
              <h3>2. Validate funnel steps</h3>
              <p>
                It is highly recommended to test your step configurations by entering a URL or a value for each step and verifying that the correct step matches the URL or value. Matching steps will be highlighted in green, and not matching steps will be highlighted in red. Typically, only one step should match a certain URL or value, as otherwise a visitor would enter multiple steps on one page.
              </p>
              <Alert
                description=" "
                message={(
                  <>
                    <p> Enter a URL or a value to validate steps. eg. http://example.com</p>
                    <Input placeholder="eg. http://example.com" onChange={(e) => setStepValidationValue(e.target.value)} />
                  </>
                )}
                type="info"
                showIcon
              />
            </Col>
            <Col span={24}>
              <h3>3. Activate funnel</h3>
              <p>

                When you activate the funnel, reports for this funnel will be generated and become available in the reporting interface and API within a few minutes or hours. Deactivating a funnel can be useful at a later point when you are no longer interested in this funnel. After you have activated the funnel, it is recommended to test your funnel configuration manually by clicking through your website or app, and then checking your visit appears in your funnel report.
              </p>

              <Form.Item name="funnelActivated" valuePropName="checked">
                <Checkbox>Activate funnel</Checkbox>
              </Form.Item>
            </Col>

            <Divider style={{ margin: 10 }} />

            <Col span={8}>
              <Form.Item name="multiAttributionEnabled" valuePropName="checked" label="Multi Channel Conversion Attribution">
                <Checkbox>Enabled</Checkbox>
              </Form.Item>
            </Col>

            <Col span={16} style={{ display: 'flex', alignItems: 'center' }}>
              <Alert style={{ flexGrow: '1' }} message="Enabling this feature gives you more accurate insights into the different referrers that lead to your conversions. Instead of only attributing a conversion to the last non direct visit, you will be able to select different attribution models to see which referrers (or channels) really contributed the most to a conversion." />
            </Col>
          </Row>
          <Page.Footer sticky>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creating || updating}>Save goal</Button>
          </Page.Footer>
        </Form>
      </Page.Content>
    </Page>
  );
};

export default EditGoal;
